
import React, { useState, useEffect } from 'react';
import { Task, MediaType, Submission } from '../types';
import { CameraIcon, MicIcon, VideoIcon, TypeIcon, ClockIcon, ArrowRightIcon, CheckIcon, MonitorIcon, WifiOffIcon } from './icons';

interface ParticipantTaskCardProps {
  task: Task;
  onStartTask: (task: Task) => void;
  hasDraft: boolean;
  hasMedia?: boolean;
  isFirstTask?: boolean;
  isCompleted?: boolean;
  isPendingUpload?: boolean;
  existingSubmission?: Submission;
  cardIndex?: number;
}

const MediaIcon: React.FC<{ type: MediaType; className?: string }> = ({ type, className = "w-6 h-6" }) => {
  switch (type) {
    case MediaType.Photo:
      return <CameraIcon className={className} />;
    case MediaType.Video:
      return <VideoIcon className={className} />;
    case MediaType.Screen:
      return <MonitorIcon className={className} />;
    case MediaType.Audio:
      return <MicIcon className={className} />;
    case MediaType.Text:
      return <TypeIcon className={className} />;
    default:
      return null;
  }
};

// Semantic chip colours — one distinct palette per media type
const getMediaTypeChipStyle = (type: MediaType): React.CSSProperties => {
  switch (type) {
    case MediaType.Photo:
      return { background: '#EAF0F7', color: '#7A93B5', border: '1px solid #D0DCE8' };
    case MediaType.Audio:
      return { background: '#F5F0E8', color: '#C2AC8E', border: '1px solid #E0D5C4' };
    case MediaType.Video:
      return { background: '#EBF0E6', color: '#6E7A5A', border: '1px solid #D4DCC8' };
    case MediaType.Text:
      return { background: '#E8F5F3', color: '#7BBFAF', border: '1px solid #C4E4DE' };
    case MediaType.Screen:
      return { background: '#EAF0F7', color: '#7A93B5', border: '1px solid #D0DCE8' };
    default:
      return { background: '#F5F3F0', color: '#A09890', border: '1px solid #E8E4DF' };
  }
};

const formatTimeLeft = (milliseconds: number): string => {
  if (milliseconds <= 0) {
    return "Past due";
  }
  const totalSeconds = Math.floor(milliseconds / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (days > 0) return `${days}d left`;
  if (hours > 0) return `${hours}h left`;
  if (minutes > 0) return `${minutes}m left`;
  return `< 1m left`;
};


const ParticipantTaskCard: React.FC<ParticipantTaskCardProps> = ({ task, onStartTask, hasDraft, hasMedia = false, isFirstTask, isCompleted = false, isPendingUpload = false, existingSubmission, cardIndex = 0 }) => {
  const [timeLeft, setTimeLeft] = useState(new Date(task.dueDate).getTime() - new Date().getTime());

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(new Date(task.dueDate).getTime() - new Date().getTime());
    }, 10000);

    return () => clearInterval(timer);
  }, [task.dueDate]);

  const isUrgent = task.isUrgent || (timeLeft < 24 * 60 * 60 * 1000 && timeLeft > 0);

  // Use first media type or submission media type for color theme
  const displayType = (isCompleted && existingSubmission)
    ? existingSubmission.mediaType
    : task.mediaTypes[0];

  // If multi-type and no media yet, use a neutral theme
  const isMultiNeutral = task.mediaTypes.length > 1 && !isCompleted && !hasMedia;

  // New Sleeker Button Styles
  const buttonClasses = hasDraft
    ? "bg-action hover:bg-action-dark shadow-sm active:scale-[0.98]"
    : "bg-action hover:bg-action/90 shadow-sm active:scale-[0.98]";

  return (
    <div
      className={`bg-white rounded-xl border border-app-border shadow-sm p-4 sm:p-6 flex flex-col gap-5 transition-all duration-300 ${isCompleted ? 'opacity-90' : 'hover:shadow-lg hover:-translate-y-0.5'} group relative overflow-hidden`}
      style={{
        animation: !isCompleted ? `slideUpFadeIn 0.5s ease-out ${cardIndex * 0.1}s both` : undefined,
      }}
      data-tutorial-id={isFirstTask ? 'task-card' : undefined}
    >
      {isCompleted && (
        <div className={`absolute top-4 right-4 rounded-full px-2.5 py-0.5 flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider ${isPendingUpload ? 'bg-status-warn-bg text-status-warn' : 'bg-status-ok-bg text-status-ok'}`}>
          {isPendingUpload ? (
            <>
              <WifiOffIcon className="w-3 h-3" />
              <span>Pending Upload</span>
            </>
          ) : (
            <>
              <CheckIcon className="w-3 h-3" />
              <span>Completed</span>
            </>
          )}
        </div>
      )}
      {/* Top Row: Icon, Type Badge, Time Left */}
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          {/* Icon Container — neutral/decorative */}
          <div className="w-12 h-12 flex-shrink-0 rounded-xl flex items-center justify-center transition-transform duration-300 group-hover:scale-105" style={{ background: '#F5F3F0', color: '#A09890' }}>
            {isMultiNeutral ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
              </svg>
            ) : (
              <MediaIcon type={displayType} className="w-6 h-6" />
            )}
          </div>
          <div className="flex flex-col justify-center gap-1.5">
            {/* Colour-coded media type chips */}
            <div className="flex flex-wrap items-center gap-1.5">
              {task.mediaTypes.map((type) => (
                <span
                  key={type}
                  className="inline-flex items-center uppercase"
                  style={{
                    ...getMediaTypeChipStyle(type),
                    borderRadius: 20,
                    padding: '3px 10px',
                    fontSize: 10,
                    fontWeight: 700,
                    gap: 5,
                  }}
                >
                  <MediaIcon type={type} className="w-3 h-3" />
                  {type}
                </span>
              ))}
              {isUrgent && !isCompleted && (
                <span
                  className="inline-flex items-center uppercase animate-urgent-pulse"
                  style={{
                    background: '#FDF0EF',
                    color: '#E8A09A',
                    border: '1px solid #F0C8C4',
                    borderRadius: 20,
                    padding: '3px 10px',
                    fontSize: 10,
                    fontWeight: 700,
                  }}
                >
                  Urgent
                </span>
              )}
            </div>
            {/* Time Estimate with Icon */}
            <div className="flex items-center gap-1.5 text-text-faint text-xs font-medium">
              <ClockIcon className="w-3.5 h-3.5" />
              <span>{task.timeEstimate}</span>
            </div>
          </div>
        </div>

        {/* Time Left Badge (Top Right) — metadata, not CTA */}
        <div
          className="flex-shrink-0 uppercase tracking-wider"
          style={{
            fontSize: 10,
            fontWeight: 600,
            padding: '3px 10px',
            borderRadius: 20,
            background: 'transparent',
            color: isUrgent ? '#E8A09A' : '#A09890',
            border: isUrgent ? '1px solid #E8A09A' : '1px solid #E8E4DF',
          }}
        >
          {formatTimeLeft(timeLeft)}
        </div>
      </div>

      {/* Middle: Prompt */}
      <div>
        <p className="text-lg text-text-ink font-bold leading-snug group-hover:text-text-ink transition-colors duration-200">
          {task.title || task.prompt}
        </p>
        {/* Recurring Task Indicator */}
        {task.recurrence && task.recurrence !== 'none' && (
          <div className="mt-2.5 flex items-center gap-2">
            <div className="flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full" style={{ color: '#A09890', background: '#F5F3F0', border: '1px solid #E8E4DF' }}>
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              <span className="uppercase tracking-wider">Repeats {task.recurrence}</span>
            </div>
            {isCompleted && (
              <span className="text-[10px] text-text-faint font-medium italic">
                ✨ Available again {task.recurrence === 'daily' ? 'tomorrow' : task.recurrence === 'weekly' ? 'next week' : 'next month'}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Bottom: Button or Completed Status */}
      <div className="mt-auto pt-1">
        {isCompleted ? (
          <div className={`w-full sm:w-auto flex items-center justify-center gap-1.5 font-bold py-2.5 px-6 rounded-xl text-xs uppercase tracking-wider ${isPendingUpload ? 'bg-status-warn-bg text-status-warn border border-status-warn' : 'bg-status-ok-bg text-status-ok border border-status-ok'}`}>
            {isPendingUpload ? (
              <>
                <WifiOffIcon className="w-4 h-4" />
                <span>Will upload when online</span>
              </>
            ) : (
              <>
                <CheckIcon className="w-4 h-4" />
                <span>Task Completed</span>
              </>
            )}
          </div>
        ) : (
          <button
            onClick={() => onStartTask(task)}
            className={`w-full sm:w-auto flex items-center justify-center gap-1.5 font-bold py-2.5 px-6 rounded-xl text-white text-xs uppercase tracking-widest transition-all transform focus:outline-none ${buttonClasses}`}
            style={{
              boxShadow: '0 4px 14px rgba(212, 113, 90, 0.25)',
            }}
            data-tutorial-id={isFirstTask ? 'start-task-button' : undefined}
          >
            <span>
              {hasDraft
                ? 'Resume Task'
                : 'Start Task'}
            </span>
            <ArrowRightIcon className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );
};

export default ParticipantTaskCard;

/* Inject keyframes into document head if not already present */
if (typeof document !== 'undefined' && !document.getElementById('task-card-animations')) {
  const style = document.createElement('style');
  style.id = 'task-card-animations';
  style.textContent = `
    @keyframes slideUpFadeIn {
      from {
        opacity: 0;
        transform: translateY(24px);
      }
      to {
        opacity: 1;
        transform: translateY(0);
      }
    }
    @keyframes subtleGlow {
      0%, 100% {
        box-shadow: 0 2px 8px rgba(212, 113, 90, 0.15);
      }
      50% {
        box-shadow: 0 4px 20px rgba(212, 113, 90, 0.25);
      }
    }
  `;
  document.head.appendChild(style);
}
